import React, { useEffect, useState } from "react";
import { Box, Flex, Spinner } from "theme-ui";
import { themevals } from "theme/themevals";
import P from "components/typography/P";
import { getAllProjectStatuses } from "api/Projects";

// A lightweight visual stage bar for the project view, inspired by the
// maintenance work-order stage strip but driven ONLY by the project's own
// status ladder (project_status.stepNumber). This is intentionally NOT the
// derived-from-five-records engine used on work orders; it reflects the
// single project status the record already carries. Company and client see
// the full ladder; a PROVIDER sees a narrowed ladder (their physical-work
// window only - see PROVIDER_RUNGS). Read-only and orienting.
//
// Segments up to and including the current status render filled (green);
// later statuses render muted. The linear timeline ALWAYS renders. Off-ladder
// states (Cancelled, Pending) are not part of the normal flow, so they are not
// shown as permanent steps - but when the project is CURRENTLY in one, that
// state is spliced into the timeline at its own stepNumber position, colored
// distinctly (amber for Pending, red for Cancelled) and marked Current, so the
// full journey stays visible with the current off-ladder state shown in place.

type Status = {
  id: string;
  name: string;
  color?: string;
  stepNumber: number;
};

// Off-ladder states: not permanent steps in the linear flow. Matched
// case-insensitively by name. Shown inline only while the project is in them.
const OFF_LADDER: Record<string, { color: string; track: string; ring: string }> = {
  cancelled: { color: "#A60000", track: "#A60000", ring: "#FDF3F3" },
  pending: { color: "#CAA11C", track: "#CAA11C", ring: "#FFFAEA" },
};

// Provider-relevant rungs. A provider only cares about the physical-work
// window - the job is scheduled, then the job is done. The office-side rungs
// before it (New / Verified / Site Ready = internal prep) and after it
// (Invoice Sent / Closed = billing internals) are not the provider's business,
// so on a provider view the ladder is narrowed to these. Matched
// case-insensitively by name. Off-ladder states (Cancelled/Pending) still
// splice in when current, so a cancelled job still reads Cancelled.
const PROVIDER_RUNGS = new Set(["scheduled", "complete"]);

const ProjectStageBar = ({
  project,
  isProvider = false,
}: {
  project: any;
  isProvider?: boolean;
}) => {
  const [statuses, setStatuses] = useState<Status[] | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const res = await getAllProjectStatuses();
      if (active) {
        setStatuses(Array.isArray(res) ? res : []);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const currentName = project?.status?.name;
  const currentId = project?.status?.id;
  const currentNameKey = typeof currentName === "string" ? currentName.toLowerCase() : "";

  // Loading: keep the space quiet, no spinner jump on a bar this small.
  if (statuses === null) {
    return (
      <Box sx={{ marginTop: "20px", marginBottom: "10px", height: "44px", display: "flex", alignItems: "center" }}>
        <Spinner size={16} strokeWidth={3} title="loading" />
      </Box>
    );
  }

  // Build the linear ladder: the permanent steps (everything that is NOT an
  // off-ladder state), ordered by stepNumber.
  const linear = statuses
    .filter((s) => !OFF_LADDER[s.name.toLowerCase()])
    .filter((s) => !isProvider || PROVIDER_RUNGS.has(s.name.toLowerCase()))
    .sort((a, b) => a.stepNumber - b.stepNumber);

  if (!linear.length) return null;

  // If the project is CURRENTLY in an off-ladder state, splice that one status
  // into the ladder at its stepNumber position so the timeline still renders in
  // full with the off-ladder state shown in place. Otherwise the ladder is just
  // the linear steps.
  const activeOffLadder = statuses.find(
    (s) =>
      OFF_LADDER[s.name.toLowerCase()] &&
      ((currentId && s.id === currentId) || s.name.toLowerCase() === currentNameKey),
  );

  const ladder: Status[] = [...linear];
  if (activeOffLadder) {
    // Insert before the first linear step whose stepNumber exceeds it; if none,
    // append at the end (terminal, e.g. a late Cancelled).
    let insertAt = ladder.findIndex((s) => s.stepNumber > activeOffLadder.stepNumber);
    if (insertAt < 0) insertAt = ladder.length;
    ladder.splice(insertAt, 0, activeOffLadder);
  }

  // Current index. Match by status ID first - it is an exact identity and
  // cannot collide, which matters because some ladders reuse a stepNumber.
  // Fall back to name, then 0.
  let currentIndex = currentId
    ? ladder.findIndex((s) => s.id === currentId)
    : -1;
  if (currentIndex < 0) {
    currentIndex = ladder.findIndex((s) => s.name.toLowerCase() === currentNameKey);
  }
  if (currentIndex < 0) {
    // On the narrowed PROVIDER ladder the project's current status is often
    // NOT one of the shown rungs (it's an office-side rung we hid). Don't
    // snap to 0 - that would falsely mark the first provider rung Current for
    // a job still in prep. Place the pointer honestly by stepNumber:
    //   - current step BELOW the lowest shown rung (still in internal prep)
    //     -> nothing current yet, every provider rung reads future (-1).
    //   - current step AT/ABOVE the highest shown rung (Complete, or the
    //     post-work Invoice Sent/Closed we hid) -> the provider's window is
    //     done; mark the last shown rung Current.
    //   - otherwise -> a normal in-window position (rare fallthrough).
    const currentStep = Number(project?.status?.stepNumber);
    if (isProvider && Number.isFinite(currentStep) && ladder.length > 0) {
      const lowest = ladder[0].stepNumber;
      const highest = ladder[ladder.length - 1].stepNumber;
      if (currentStep < lowest) {
        currentIndex = -1; // pre-window: all rungs future, none done
      } else if (currentStep >= highest) {
        currentIndex = ladder.length - 1; // window complete
      } else {
        // Between shown rungs: last rung at or before the current step.
        let idx = -1;
        for (let k = 0; k < ladder.length; k++) {
          if (ladder[k].stepNumber <= currentStep) idx = k;
        }
        currentIndex = idx;
      }
    } else {
      currentIndex = 0;
    }
  }

  return (
    <Box
      sx={{
        marginTop: "20px",
        marginBottom: "20px",
        overflowX: ["auto", "visible"],
        border: `1px solid ${themevals.colors.inputOutline}`,
        borderRadius: "10px",
        background: themevals.colors.base_100,
        padding: ["18px 16px", "22px 24px"],
      }}
    >
      <Flex sx={{ gap: ["10px", "14px"], minWidth: ["640px", "auto"], alignItems: "flex-start" }}>
        {ladder.map((s, i) => {
          const done = i < currentIndex;
          const current = i === currentIndex;
          const off = OFF_LADDER[s.name.toLowerCase()];
          // Color ramp matching the maintenance stage strip: done = solid green,
          // current = solid green (strongest), future = muted track. Pill shape
          // with fully rounded ends. An off-ladder segment (only ever present
          // when it is the current state) uses its own color (amber Pending /
          // red Cancelled) instead of green. No per-stage percentage (the
          // project carries a single current-status pointer, not per-record
          // fill); the sub-line states the stage state instead.
          const fill = off
            ? off.track
            : done || current
            ? themevals.colors.secondary
            : themevals.colors.base_300;
          const ringColor = off ? off.ring : themevals.colors.base_200;
          const borderColor = off ? off.color : themevals.colors.success;
          const stateLabel = current ? "Current" : done ? "Done" : "";
          const stateColor = off ? off.color : current ? themevals.colors.secondary : themevals.colors.dkgrey;
          return (
            <Flex key={s.id} sx={{ flexDirection: "column", flex: 1, gap: "10px", alignItems: "center" }}>
              <Box
                sx={{
                  width: "100%",
                  height: "10px",
                  borderRadius: "999px",
                  backgroundColor: fill,
                  border: current ? `1px solid ${borderColor}` : "1px solid transparent",
                  boxShadow: current ? `0 0 0 3px ${ringColor}` : "none",
                }}
              />
              <Flex sx={{ flexDirection: "column", alignItems: "center", gap: "2px", textAlign: "center" }}>
                <P
                  fontFamily="Poppins"
                  fontSize="12px"
                  fontWeight={current ? "600" : "500"}
                  marginTop="0px"
                  marginBottom="0px"
                  color={off ? off.color : current || done ? themevals.colors.black : themevals.colors.dkgrey}
                >
                  {s.name}
                </P>
                {stateLabel && (
                  <P
                    fontFamily="Poppins"
                    fontSize="11px"
                    fontWeight={current ? "600" : "400"}
                    marginTop="0px"
                    marginBottom="0px"
                    color={stateColor}
                  >
                    {stateLabel}
                  </P>
                )}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
};

export default ProjectStageBar;
